import './instrument'; // Must be first: initializes Sentry before React loads.
import React from 'react';
import './utils/disableConsole';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { ErrorBoundary as SentryErrorBoundary, captureException } from '@sentry/react';
import App from './App';
import './index.css';

// TypeScript 4.9 + @sentry/react v9 type compatibility workaround.
const ErrorBoundary = SentryErrorBoundary as any;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 0,
      cacheTime: 1000 * 60 * 5,
      // Report uncaught query errors to Sentry so failed data fetches are visible.
      onError: (error: any) => {
        captureException(error, {
          tags: { source: 'react-query' },
          extra: { query: error?.config?.url },
        });
      },
    },
    mutations: {
      onError: (error: any) => {
        captureException(error, {
          tags: { source: 'react-query-mutation' },
        });
      },
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ErrorBoundary
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
          <div className="max-w-md rounded-3xl border border-gray-200 bg-white p-8 shadow-sm text-center">
            <div className="mb-4 text-5xl">💥</div>
            <h1 className="mb-2 text-xl font-semibold text-gray-900">Something went wrong</h1>
            <p className="mb-6 text-sm text-gray-500">
              The error has been reported automatically. Please reload the page to try again.
            </p>
            <button
              className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
              onClick={() => window.location.reload()}
            >
              Reload page
            </button>
          </div>
        </div>
      }
      showDialog={false}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <App />
          <Toaster 
            position="top-right"
            gutter={8}
            containerClassName="z-50"
            toastOptions={{
              duration: 5000,
              className: 'backdrop-blur-lg',
              style: {
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))',
                color: '#1f2937',
                borderRadius: '16px',
                padding: '16px 20px',
                fontSize: '14px',
                fontWeight: '500',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(16px)',
                maxWidth: '420px',
                minHeight: '60px',
              },
              success: {
                style: {
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.95), rgba(5, 150, 105, 0.9))',
                  color: '#ffffff',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                },
                iconTheme: {
                  primary: '#ffffff',
                  secondary: 'rgba(16, 185, 129, 1)',
                },
              },
              error: {
                style: {
                  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.95), rgba(220, 38, 38, 0.9))',
                  color: '#ffffff',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                },
                iconTheme: {
                  primary: '#ffffff',
                  secondary: 'rgba(239, 68, 68, 1)',
                },
              },
              loading: {
                style: {
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.95), rgba(37, 99, 235, 0.9))',
                  color: '#ffffff',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                },
                iconTheme: {
                  primary: '#ffffff',
                  secondary: 'rgba(59, 130, 246, 1)',
                },
              },
            }}
          />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
); 
